import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import React, { useEffect, useMemo, useRef } from 'react';
import { useFormik } from 'formik';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import moment from 'moment';

import FormSimpleSelectField from 'components/ui/form/FormSimpleSelectField';
import StatusBadge from 'components/ui/StatusBadge';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useFormSubmit from 'hooks/useFormSubmit';

// Shablon tanlovida "Erkin matn" varianti
const FREE_TEXT = 0;

// Backend status_code → badge rangi
const STATUS_VARIANT = {
  delivered: 'success',
  pending: 'warning',
  failed: 'danger',
};

const validationSchema = yup.object({
  phone: yup.string().required('settings.sentMessages.validation.phone'),
  text: yup.string().trim().required('settings.sentMessages.validation.text'),
});

const digits = (phone) => String(phone || '').replace(/\D/g, '');

/**
 * Shartnoma ichidagi SMS oynasi: yuborilgan SMSlar ro'yxati (sana, yuboruvchi, status)
 * va shu yerdan SMS yuborish — erkin matn, to'ldirilgan shablon ("Shartnoma tuzildi",
 * "Shartnoma bekor qilish") yoki yuborilmay qolganini qayta yuborish; raqam sifatida
 * mijozning asosiy yoki qo'shimcha raqami tanlanadi.
 */
const SentMessages = (props) => {
  const { open, setOpen, data } = props;
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const { submit, isSubmitting } = useFormSubmit();
  const formRef = useRef(null);
  const contractId = data?.id;

  // Yuborish uchun tayyor ma'lumot: mijoz raqamlari + shartnoma bo'yicha to'ldirilgan shablonlar
  const optionsQuery = useQuery({
    queryKey: ['/admin/contract/smsoptions', contractId],
    queryFn: async () => {
      const response = await axiosPrivate.get(
        `/admin/contract/smsoptions/${contractId}`,
      );
      return response.data.data;
    },
    enabled: !!contractId && open,
  });
  const phones = useMemo(
    () => optionsQuery.data?.phones || [],
    [optionsQuery.data],
  );
  const templates = useMemo(
    () => optionsQuery.data?.templates || [],
    [optionsQuery.data],
  );

  // Yuborilgan SMSlar
  const listQuery = useQuery({
    queryKey: ['/admin/contract/sms', contractId],
    queryFn: async () => {
      const response = await axiosPrivate.get(
        `/admin/contract/sms/${contractId}`,
      );
      return response.data.data;
    },
    enabled: !!contractId && open,
  });
  const messages = listQuery.data || [];

  const formik = useFormik({
    initialValues: { phone: '', template: FREE_TEXT, text: '' },
    validationSchema,
    onSubmit: async (values) => {
      submit(
        { type: 'post', contentType: 'simple' },
        { phone: values.phone, text: values.text },
        `/admin/contract/smssend/${contractId}`,
        { title: t('settings.sentMessages.sent') },
        null,
        true,
        () => {
          formik.setFieldValue('text', '');
          formik.setFieldValue('template', FREE_TEXT);
          listQuery.refetch();
        },
      );
    },
  });

  // Asosiy raqam default tanlangan bo'lsin
  useEffect(() => {
    if (phones.length > 0 && !formik.values.phone) {
      formik.setFieldValue('phone', phones[0].phone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phones]);

  const phoneOptions = useMemo(
    () =>
      phones.map((item) => ({
        phone: item.phone,
        label: `${t(
          item.key === 'phone2'
            ? 'settings.sentMessages.secondPhone'
            : 'settings.sentMessages.mainPhone',
        )} — ${item.phone}`,
      })),
    [phones, t],
  );

  const templateOptions = useMemo(
    () => [
      { id: FREE_TEXT, name: t('settings.sentMessages.freeText') },
      ...templates.map((item) => ({
        id: item.id,
        name: item.status
          ? item.name
          : `${item.name} (${t('settings.sentMessages.inactiveTemplate')})`,
      })),
    ],
    [templates, t],
  );

  // Shablon tanlanganda matn to'ldiriladi (keyin tahrirlash mumkin)
  const onTemplateChange = (value) => {
    formik.setFieldValue('template', value);
    const template = templates.find(
      (item) => String(item.id) === String(value),
    );
    formik.setFieldValue('text', template ? template.text : '');
  };

  // Qayta yuborish — matn va raqam formaga tushadi; xodim raqamni o'zgartirishi mumkin
  const onResend = (row) => {
    const match = phones.find((item) => digits(item.phone) === digits(row.phone));
    formik.setFieldValue('phone', match ? match.phone : phones[0]?.phone || '');
    formik.setFieldValue('template', FREE_TEXT);
    formik.setFieldValue('text', row.text || '');
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  const headCells = [
    t('common.fields.date'),
    t('common.table.receiver'),
    t('common.table.sender'),
    t('common.table.status'),
    t('common.table.sms'),
    '',
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      maxWidth='lg'
      disableEscapeKeyDown={true}
      PaperProps={{
        className: '!base-border !shadow-none min-w-[75vw]',
      }}
    >
      <DialogTitle
        id='alert-dialog-title'
        className='!text-center !mb-5 !bg-[#CDCDCDC7]'
      >
        <span className='mr-4'>{t('settings.sentMessages.title')}</span>
      </DialogTitle>

      <DialogContent>
        <div className='h-[calc(100vh-180px)] overflow-y-auto my-scroll'>
          {/* SMS yuborish */}
          <div
            ref={formRef}
            className='base-border rounded-lg p-4 mb-4 bg-white'
          >
            <div className='text-[15px] font-semibold text-gray-700 mb-3'>
              {t('settings.sentMessages.send')}
            </div>
            {phones.length === 0 && !optionsQuery.isLoading ? (
              <div className='text-gray-400 text-sm'>
                <i className='bi bi-exclamation-octagon mr-1' />
                {t('settings.sentMessages.noPhone')}
              </div>
            ) : (
              <form onSubmit={formik.handleSubmit}>
                <Grid
                  container
                  spacing={{ xs: 2, sm: 2, md: 2 }}
                  rowSpacing={1}
                  columns={{ xs: 12, sm: 12, md: 12 }}
                >
                  <Grid item={true} md={6} sm={12} xs={12}>
                    <FormSimpleSelectField
                      delay={0}
                      label={t('settings.sentMessages.receiver')}
                      fieldName='phone'
                      formik={formik}
                      options={phoneOptions}
                      itemValue='phone'
                      itemLabel='label'
                    />
                  </Grid>
                  <Grid item={true} md={6} sm={12} xs={12}>
                    <FormSimpleSelectField
                      delay={0}
                      label={t('settings.sentMessages.template')}
                      fieldName='template'
                      formik={formik}
                      options={templateOptions}
                      itemValue='id'
                      itemLabel='name'
                      changeFn={onTemplateChange}
                    />
                  </Grid>
                  <Grid item={true} sm={12} xs={12}>
                    <label
                      htmlFor='contract-sms-text'
                      className='block text-xs text-gray-500 mb-1'
                    >
                      {t('settings.sentMessages.text')}
                    </label>
                    <textarea
                      id='contract-sms-text'
                      name='text'
                      rows={4}
                      className={`w-full base-border rounded-lg px-3 py-2 text-sm text-gray-700 bg-white outline-none focus:border-[#0057FF] resize-y disabled:bg-gray-50 ${
                        formik.touched.text && formik.errors.text
                          ? '!border-red-500'
                          : ''
                      }`}
                      value={formik.values.text}
                      onChange={(e) => formik.setFieldValue('text', e.target.value)}
                      onBlur={() => formik.setFieldTouched('text', true)}
                      disabled={isSubmitting}
                    />
                    {formik.touched.text && formik.errors.text ? (
                      <div className='text-xs text-red-500 mt-1'>
                        {t(formik.errors.text)}
                      </div>
                    ) : null}
                  </Grid>
                  <Grid item={true} sm={12} xs={12}>
                    <div className='flex items-center justify-end gap-3'>
                      <span className='text-xs text-gray-400'>
                        {formik.values.text?.length || 0}
                      </span>
                      <Button
                        type='submit'
                        variant='contained'
                        color='primary'
                        disabled={isSubmitting}
                        startIcon={
                          isSubmitting ? (
                            <CircularProgress size={16} color='inherit' />
                          ) : (
                            <i className='bi bi-send' />
                          )
                        }
                      >
                        {t('settings.sentMessages.sendButton')}
                      </Button>
                    </div>
                  </Grid>
                </Grid>
              </form>
            )}
          </div>

          {/* Yuborilgan SMSlar */}
          <TableContainer
            className='tab-table-container !shadow-none !base-border'
            component='div'
          >
            <Table stickyHeader component='div'>
              <TableHead component='div'>
                <TableRow component='div'>
                  {headCells.map((label, i) => (
                    <TableCell key={i} align='left'>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody className='overflow-hidden !bg-white' component='div'>
                {listQuery.isLoading ? (
                  <TableRow component='div'>
                    <TableCell colSpan={6}>
                      <div className='flex items-center justify-center py-6'>
                        <CircularProgress size={24} color='inherit' />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : messages.length > 0 ? (
                  messages.map((row) => {
                    const failed = row.status_code === 'failed';
                    return (
                      <TableRow hover tabIndex={-1} key={row.id} component='div'>
                        <TableCell align='left' className='whitespace-nowrap'>
                          {row.created_at
                            ? moment(row.created_at).format('DD.MM.YYYY HH:mm')
                            : ''}
                        </TableCell>
                        <TableCell align='left' className='whitespace-nowrap'>
                          {row.phone}
                        </TableCell>
                        <TableCell align='left' className='whitespace-nowrap'>
                          {row.staff?.name || (
                            <span className='text-gray-400'>
                              {t('settings.sentMessages.auto')}
                            </span>
                          )}
                        </TableCell>
                        <TableCell align='left'>
                          <span title={row.status || ''}>
                            <StatusBadge
                              variant={STATUS_VARIANT[row.status_code] || 'neutral'}
                            >
                              {t(
                                `common.typeList.sentMessages.${
                                  row.status_code || 'pending'
                                }`,
                              )}
                            </StatusBadge>
                          </span>
                        </TableCell>
                        <TableCell align='left'>{row.text}</TableCell>
                        <TableCell align='right' className='whitespace-nowrap'>
                          <Button
                            size='small'
                            variant='base'
                            color={failed ? 'primary' : 'default'}
                            className='!text-[12px]'
                            startIcon={<i className='bi bi-arrow-repeat' />}
                            onClick={() => onResend(row)}
                          >
                            {t('settings.sentMessages.resend')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow component='div'>
                    <TableCell colSpan={6}>
                      <span className='text-gray-400 flex items-center justify-center py-4'>
                        <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                        {t('common.global.noDataFound')}
                      </span>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <div className='text-right text-sm text-gray-500 mt-2 pr-2'>
            {t('common.table.total')}: {messages.length}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SentMessages;
